import {
  Children,
  isValidElement,
  type PropsWithChildren,
  type ReactElement,
  type ReactNode,
} from 'react'
import { DefinitionListColumn } from './DefinitionListColumn.client'
import { DefinitionListItem, type DefinitionListItemProps } from './DefinitionListItem.client'

export const DEFINITION_LIST_ITEM_DISPLAY_NAME = 'DefinitionList.Item'
export const DEFINITION_LIST_COLUMN_DISPLAY_NAME = 'DefinitionList.Column'

function matchesCompoundChild(
  child: ReactNode,
  component: unknown,
  displayName: string,
): child is ReactElement {
  if (!isValidElement(child)) {
    return false
  }

  if (child.type === component) {
    return true
  }

  const childType = child.type

  return (
    typeof childType === 'function' &&
    'displayName' in childType &&
    childType.displayName === displayName
  )
}

export function isDefinitionListColumn(child: ReactNode): child is ReactElement<PropsWithChildren> {
  return matchesCompoundChild(child, DefinitionListColumn, DEFINITION_LIST_COLUMN_DISPLAY_NAME)
}

export function isDefinitionListItem(
  child: ReactNode,
): child is ReactElement<DefinitionListItemProps> {
  return matchesCompoundChild(child, DefinitionListItem, DEFINITION_LIST_ITEM_DISPLAY_NAME)
}

export function getColumnChildren(children: ReactNode) {
  return Children.toArray(children).filter(isDefinitionListColumn)
}

export function getListItems(children: ReactNode) {
  return Children.toArray(children).filter(isDefinitionListItem)
}

export function getColumnItems(column: ReactElement<PropsWithChildren>) {
  return getListItems(column.props.children)
}

export function getColumnId(
  column: ReactElement<PropsWithChildren & { id?: string }>,
  columnIndex: number,
) {
  if (column.props.id) {
    return column.props.id
  }

  if (column.key != null) {
    return String(column.key)
  }

  return `column-${columnIndex}`
}

export function getRowKey(
  columnItems: ReactElement<DefinitionListItemProps>[][],
  rowIndex: number,
) {
  const itemKey = columnItems.map((items) => items[rowIndex]?.key).find((key) => key != null)

  if (itemKey != null) {
    return String(itemKey)
  }

  return columnItems
    .map((items) => items[rowIndex]?.props.children)
    .filter((child) => child != null && child !== false)
    .join('-')
}

export function parseDefinitionListChildren(children: ReactNode) {
  const columnChildren = getColumnChildren(children)
  const hasColumns = columnChildren.length > 0

  return {
    columnChildren,
    columnCount: hasColumns ? columnChildren.length : 1,
    hasColumns,
    listItems: getListItems(children),
  }
}
